import { useState } from "react";

import useGetAllUsers from "../../hooks/useGetAllUsers";
import useGetUsersByName from "../../hooks/useGetUsersByName";
import useDeleteUsersById from "../../hooks/useDeleteUsersById";

import Spinner from "../spinner/Spinner";

import ModalIcon from "../../assets/img/dashboard/modal-icon.png";

import UsersTableStyles from "./UsersTableStyles.module.css";

type User = {
  id: number;
  preferredName: string;
  lastName: string;
  email: string;
};

export const UsersTable = () => {
  // States
  const [input, setInput] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [deleteMode, setDeleteMode] = useState<"single" | "multiple" | null>(
    null,
  );

  // Custom Hooks
  const { getAllUsersData, isLoading: isGetAllUsersDataLoading } =
    useGetAllUsers();
  const { getUsersByNameData, isLoading: isGetUsersByNameDataLoading } =
    useGetUsersByName(input.trim());
  const { mutate: deleteUsers, isPending: isDeleting } = useDeleteUsersById();

  //
  const hasSelected = selectedIds.length > 0;
  const isLoading = isGetAllUsersDataLoading || isGetUsersByNameDataLoading;

  //
  const filteredUsers = input.trim()
    ? getUsersByNameData?.foundUsers || []
    : getAllUsersData?.users || [];

  //
  const handleCheckboxChange = (userId: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId),
    );
  };

  const deleteByRow = () => {
    if (userToDelete !== null) {
      deleteUsers([userToDelete], {
        onSuccess: () => {
          setSelectedIds([]);
          setUserToDelete(null);
          setDeleteMode(null);
          setInput("");
        },
      });
    }
  };

  const deleteBySelected = () => {
    deleteUsers(selectedIds, {
      onSuccess: () => {
        setSelectedIds([]);
        setUserToDelete(null);
        setDeleteMode(null);
        setInput("");
      },
    });
  };

  const closeModal = () => {
    setDeleteMode(null);
    setUserToDelete(null);
  };

  return (
    <>
      {deleteMode && (
        <div className={UsersTableStyles.deletePrompt}>
          <div className={UsersTableStyles.deletePromptMainContainer}>
            <span
              aria-label="Close Modal"
              onClick={closeModal}
              className="fa-solid fa-xmark"
            />

            <img
              src={ModalIcon}
              alt="Modal icon"
              className={UsersTableStyles.deleteIcon}
            />

            <h1>
              {deleteMode === "single"
                ? "Are you sure you want to delete this User?"
                : `You are about to remove ${selectedIds.length} user(s) from Gamechanger.`}
            </h1>

            <button
              type="button"
              disabled={isDeleting}
              onClick={deleteMode === "single" ? deleteByRow : deleteBySelected}
            >
              {isDeleting ? (
                <Spinner variant="small-spinner" alignSpin="center-spinner" />
              ) : (
                "YES"
              )}
            </button>
          </div>
        </div>
      )}

      <div className={UsersTableStyles.container}>
        <div className={UsersTableStyles.heading}>
          <h1>Users</h1>

          <a href="/users/create">
            <i className="fa-solid fa-plus"></i>Create New User
          </a>
        </div>

        <div className={UsersTableStyles.input}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z"
              stroke="white"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search by preferred name..."
          />
        </div>

        {isLoading ? (
          <div style={{ display: "block", padding: "20px 0px" }}>
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <p className={UsersTableStyles.emptyListMessage}>
            There are no Users available.
          </p>
        ) : (
          <div className={UsersTableStyles.tableContainer}>
            <table className={UsersTableStyles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Preferred Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user: User) => (
                  <tr key={user.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${user.preferredName} ${user.lastName}`}
                        checked={selectedIds.includes(user.id)}
                        onChange={(e) =>
                          handleCheckboxChange(user.id, e.target.checked)
                        }
                      />
                    </td>
                    <td>{user.preferredName}</td>
                    <td>{user.lastName}</td>
                    <td>{user.email}</td>
                    <td>
                      <button
                        type="button"
                        title="Delete this User"
                        aria-label="Delete this User"
                        className={UsersTableStyles.deleteButton}
                        onClick={() => {
                          setUserToDelete(user.id);
                          setDeleteMode("single");
                        }}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={UsersTableStyles.footer}>
          <p>
            {selectedIds.length} of {getAllUsersData?.users.length || 0} row(s)
            selected.
          </p>

          {hasSelected && (
            <button
              disabled={isDeleting}
              onClick={() => setDeleteMode("multiple")}
            >
              Delete Selected
            </button>
          )}
        </div>
      </div>
    </>
  );
};
